<script setup lang="ts">
import { breakpointsTailwind, useBreakpoints } from '@vueuse/core'
import { UDrawer, UModal } from '#components'

/**
 * The surface a customer commits on.
 *
 * Configuring a server and buying it are two different jobs. Configuring is
 * exploratory and reversible; saying who you are and paying is linear and
 * focused. They used to share one scroll, so the commit step inherited every
 * distraction of the browse step — and on a phone the most important button in
 * the funnel rendered at the bottom of a long page, immediately above a fixed
 * bar and beside a floating assistant button, with the keyboard up.
 *
 * So the page stays the configurator, and everything from "who is buying" to
 * "your server is on its way" happens here: identity, the second factor,
 * account selection, review, and waiting for payment. One surface, one focus
 * trap, one place the total is always visible.
 *
 * It is an OVERLAY, not a route, and the distinction is load-bearing. The
 * public checkout deliberately stopped handing customers over to a second page
 * mid-purchase; this does not bring that back. The page stays mounted, the
 * draft is untouched, and dismissing puts the customer back exactly where they
 * were with nothing lost.
 *
 * Chrome only. Every step is rendered by the page through the default slot,
 * because those steps need page state (the session, the quote, the draft) and
 * threading it all through here would make this a second copy of the checkout.
 */
const props = withDefaults(
  defineProps<{
    open: boolean
    /** The accessible name; also the visible heading. */
    title: string
    /**
     * One line that is always true and always visible: the price, and enough
     * of the machine to recognise it. On a phone the order summary has to be a
     * collapsible at the top — never a block the customer scrolls past to
     * reach the form — and this is its collapsed state.
     */
    summaryLine?: string | null
    /** Open the summary without being asked. True on the review step, where
     *  the summary IS the content rather than a reminder of it. */
    summaryExpanded?: boolean
    /** Rendered as a slim indicator; omit for a single-step sheet. */
    steps?: { key: string; label: string }[]
    currentStep?: string | null
    /**
     * Sub-position inside the step the customer is on, when that step is
     * itself a short form.
     *
     * Folded into the active chip instead of being drawn as a second rail. The
     * signup wizard used to draw its own, so the sheet showed two step
     * components stacked — and the one describing the purchase was the one
     * that mattered least at that moment. This keeps ONE rail while still
     * answering "how much more of this is there?", which is the question a
     * nine-field billing address loses people on.
     */
    currentStepProgress?: { index: number, total: number } | null
    /** False while something is in flight that a stray Escape would strand. */
    dismissible?: boolean
    /**
     * Whether this step has an action of its own.
     *
     * Declared rather than inferred from the slot: a `<template #footer>` the
     * page renders conditionally still leaves the slot defined, so testing for
     * it would draw an empty bordered strip under the payment step — which
     * carries its own buttons.
     */
    showFooter?: boolean
  }>(),
  {
    summaryLine: null,
    summaryExpanded: false,
    steps: () => [],
    currentStep: null,
    currentStepProgress: null,
    dismissible: true,
    showFooter: true,
  },
)

const emit = defineEmits<{ 'update:open': [boolean] }>()

const slots = defineSlots<{
  /** The step's content. */
  default: () => any
  /** The expanded order summary, in the header. */
  summary?: () => any
  /** The step's primary action. Sits in a footer that never scrolls away. */
  footer?: () => any
}>()

/**
 * A phone gets a bottom sheet, a desktop gets a dialog.
 *
 * Not cosmetic. `CheckoutMobileBar` is `fixed bottom-0 z-40` and the assistant
 * FAB sits beside it, so a centred dialog on a phone puts the primary action
 * in the middle of the screen with two other controls fighting for the bottom
 * of it. A bottom sheet covers both and puts confirm under the thumb.
 */
const breakpoints = useBreakpoints(breakpointsTailwind)
const isDesktop = breakpoints.greaterOrEqual('sm')

/**
 * `update:open` means the CUSTOMER opened or dismissed the sheet — never an
 * echo of the `open` the page already set.
 *
 * The two shapes above disagree about that. `UModal` emits on a gesture and
 * nothing else. `UDrawer` (vaul-vue) watches its own state with
 * `immediate: true`, so it emits `false` the moment it MOUNTS, and again after
 * every change the page makes through `open`. The page answers `false` with
 * `closeCommit()`, which hands focus to the CTA without `preventScroll` — and
 * this component mounts as soon as a plan is chosen. So on a phone, choosing a
 * plan threw the page to the very bottom (measured: 4,266px on a Pixel 7), and
 * every close the page makes itself — `editFromCommit`, behind the review
 * step's "change" links — took focus off the step it had just brought into
 * view and gave it to the CTA, scrolling there too whenever the CTA was off
 * screen.
 */
function onOpenChange(value: boolean) {
  if (value !== props.open) emit('update:open', value)
}

/**
 * `100dvh`, never `100vh`.
 *
 * iOS Safari measures `vh` against the viewport with its toolbars HIDDEN, so a
 * `100vh` sheet is taller than the screen and the sticky footer — the button
 * this whole component exists to put under the customer's thumb — sits below
 * the fold until they scroll. `dvh` follows the toolbars as they come and go.
 * The drawer already reserves `mt-24`, hence 88 rather than 100.
 */
/**
 * `100dvh`, never `100vh`.
 *
 * iOS Safari measures `vh` against the viewport with its toolbars HIDDEN, so a
 * `100vh` sheet is taller than the screen and the sticky footer — the button
 * this whole component exists to put under the customer's thumb — sits below
 * the fold until they scroll. `dvh` follows the toolbars as they come and go.
 *
 * `mt-8` overrides the drawer theme's own `mt-24`. That 96px gap is the right
 * hint for a sheet you flick up to read; it is 96px stolen from a form. With
 * the gap and the summary both taking their default share, the scrolling part
 * of the body measured 173px on a 390x844 screen against 607px of content —
 * a form read through a letterbox.
 */
const containerUi = computed(() =>
  isDesktop.value
    ? { content: 'sm:max-w-3xl max-h-[calc(100dvh-4rem)]' }
    : { content: 'mt-8 h-[92dvh] max-h-[92dvh]' },
)

/**
 * Collapsed by default, and reset on every OPENING rather than at setup.
 *
 * This component is mounted as soon as there is a plan to buy, long before it
 * is opened, and at that point the step is whatever the page's ref happens to
 * hold. Seeding from the prop at setup therefore expanded the summary on the
 * identity step — which on a phone put the whole order breakdown above a
 * nine-field form and pushed the fields off the screen.
 */
const expanded = ref(false)

/**
 * `expanded` is a MOBILE concern only.
 *
 * On desktop the order lives in the aside and is always visible, so there is
 * nothing to expand. On a phone there is no second column, and opening the
 * summary by default left 173px of scrollable body against 607px of content —
 * for information the collapsed line and the confirm button already carry. So
 * it stays shut until asked for.
 */
watch(
  () => props.open,
  (open) => {
    if (open) expanded.value = false
  },
)

const currentIndex = computed(() => {
  const i = props.steps.findIndex((s) => s.key === props.currentStep)
  return i < 0 ? 0 : i
})

const currentLabel = computed(() => props.steps[currentIndex.value]?.label ?? '')

/**
 * How far through the whole commit, sub-steps included.
 *
 * Steps behind the customer count whole; the one they are on counts by its own
 * sub-progress when it has any (the signup wizard's password → profile →
 * address). That is what the old "1/3" chip was saying in words, and putting it
 * in the bar instead means there is one number to read rather than two that
 * have to be reconciled.
 */
const progressFraction = computed(() => {
  const total = props.steps.length
  if (total === 0) return 0
  const sub = props.currentStepProgress
  const within = sub && sub.total > 0 ? sub.index / sub.total : 0
  return Math.min(1, (currentIndex.value + within) / total)
})

/**
 * Keep the focused field off the sticky footer.
 *
 * A long form inside a fixed-height container is exactly where mobile keyboards
 * break: the browser scrolls the field into the *visual* viewport, which on a
 * phone is the part our own footer is sitting on top of. Centring it in our
 * scroller instead is the only thing that reliably clears it.
 */
function onFocusIn(event: FocusEvent) {
  const el = event.target as HTMLElement | null
  if (!el?.matches?.('input, textarea, select')) return
  if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
    el.scrollIntoView({ block: 'center' })
    return
  }
  el.scrollIntoView({ behavior: 'smooth', block: 'center' })
}
</script>

<template>
  <component
    :is="isDesktop ? UModal : UDrawer"
    :open="open"
    :title="title"
    :dismissible="dismissible"
    :ui="containerUi"
    @update:open="onOpenChange"
  >
    <!-- `#content` because this panel owns its own chrome: three regions, and
         only the middle one scrolls. That is what lets the total stay pinned
         above and the primary action stay pinned below, which is the whole
         point of moving the form off the page. -->
    <!-- `#content` because this panel owns its own chrome.

         Desktop is TWO COLUMNS: the order beside the step, not above it.
         Stacked, the summary was 323px of an 836px modal on the review step
         and the part the customer had to act in was 288px — the thing being
         bought took more room than the thing being done. Beside, it costs no
         vertical space at all and stays visible while they type.

         Mobile has no room for a second column, so there the order is the
         collapsible line in the header. -->
    <template #content>
      <div
        data-testid="checkout-commit-sheet"
        class="flex min-h-0 flex-1 flex-col bg-default sm:flex-row"
      >
        <!-- ── The order, beside the step (desktop only) ────────────────── -->
        <!-- Two panes, two SURFACES.
             `bg-elevated/30` was the wrong choice and dark mode proved it: in
             dark, `--ui-bg-muted` and `--ui-bg-elevated` are both
             `neutral-800`, so a 30% wash of it over `neutral-900` is almost
             nothing — and `--ui-border` is ALSO `neutral-800`, so the divider
             disappeared into the pane it was meant to divide. Full `bg-muted`
             is a real step against `bg-default` in both themes
             (neutral-50/white in light, neutral-800/neutral-900 in dark), and
             `border-muted` (neutral-200 / neutral-700) is lighter than both
             surfaces in both, so the seam is visible either way.

             `bg-elevated` rather than `bg-muted` for the aside because the two
             are identical in dark (both neutral-800) and NOT in light, where
             elevated is neutral-100 against muted's neutral-50 — measured, a
             0.033 OKLCH-lightness step from white instead of 0.016. Light is
             the default theme; it should not be the one leaning entirely on a
             1px line.

             Named for assistive tech too, because "clear separation" is not
             only a visual property: a landmark with a heading is how this
             reads as a distinct region rather than more of the form. -->
        <aside
          v-if="isDesktop && slots.summary"
          aria-labelledby="commit-summary-heading"
          class="w-[17rem] shrink-0 overflow-y-auto overscroll-contain border-r border-muted bg-elevated p-5"
        >
          <h3
            id="commit-summary-heading"
            class="mb-3 text-[11px] font-semibold uppercase tracking-wide text-muted"
          >
            {{ $t('billing.page.checkout.commit.summaryHeading') }}
          </h3>
          <slot name="summary" />
        </aside>

        <div class="flex min-h-0 flex-1 flex-col bg-default">
          <!-- ── Header: who is doing what, and where they are ─────────── -->
          <div class="shrink-0 border-b border-default">
            <div class="flex items-start gap-2 p-4 sm:px-6">
              <div class="min-w-0 flex-1">
                <h2 class="text-base font-semibold">{{ title }}</h2>
                <!-- Mobile's summary: one line, always true, always on screen
                     — the reassurance that the form has a reason. On desktop
                     the aside says it in full, so this would be saying it
                     twice. -->
                <button
                  v-if="summaryLine && !isDesktop"
                  type="button"
                  class="mt-0.5 flex max-w-full items-center gap-1 text-left text-sm text-muted hover:text-default"
                  :aria-expanded="expanded"
                  aria-controls="commit-summary"
                  @click="expanded = !expanded"
                >
                  <span class="truncate">{{ summaryLine }}</span>
                  <UIcon
                    :name="expanded ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'"
                    class="size-4 shrink-0"
                  />
                </button>
              </div>
              <UButton
                size="sm"
                variant="ghost"
                color="neutral"
                icon="i-lucide-x"
                class="shrink-0"
                :aria-label="$t('billing.page.checkout.commit.close')"
                @click="emit('update:open', false)"
              />
            </div>

            <!-- Where they are: one line and a bar.
                 This was a rail of numbered chips with labels and connectors,
                 and it did not fit. The step column is ~496px once the order
                 aside has its 272px, and "1 Who's buying 1/3 — 2 Review and
                 confirm — 3 Payment" is wider than that — so it grew a
                 horizontal scrollbar, on a modal, for three steps. Hiding the
                 inactive labels on phones only moved the problem to desktop.

                 A sentence and a progress bar cannot overflow at any width,
                 say the same thing in less space, and need no per-breakpoint
                 divergence. The bar carries the sub-step too, which is what
                 the "1/3" was doing in words — so a customer three fields into
                 the signup sees the bar move without a second counter to
                 reconcile against the first. -->
            <div
              v-if="steps.length > 1"
              class="border-t border-default px-4 py-2.5 sm:px-6"
            >
              <p class="flex items-baseline gap-1.5 text-xs">
                <span class="shrink-0 font-medium text-default">
                  {{
                    $t('billing.page.checkout.commit.stepCounter', {
                      n: currentIndex + 1,
                      total: steps.length,
                    })
                  }}
                </span>
                <span class="min-w-0 truncate text-muted">{{ currentLabel }}</span>
              </p>
              <div
                class="mt-1.5 h-1 w-full overflow-hidden rounded-full bg-accented"
                role="progressbar"
                :aria-valuemin="0"
                :aria-valuemax="100"
                :aria-valuenow="Math.round(progressFraction * 100)"
                :aria-label="
                  $t('billing.page.checkout.commit.stepCounter', {
                    n: currentIndex + 1,
                    total: steps.length,
                  })
                "
              >
                <div
                  class="h-full rounded-full bg-primary transition-[width] duration-300 ease-out"
                  :style="{ width: `${Math.round(progressFraction * 100)}%` }"
                />
              </div>
            </div>

            <!-- Mobile's expanded order. -->
            <div
              v-if="!isDesktop && expanded && slots.summary"
              id="commit-summary"
              class="max-h-[40dvh] overflow-y-auto overscroll-contain border-t border-default px-4 py-3 sm:px-6"
            >
              <slot name="summary" />
            </div>
          </div>

          <!-- ── Body: the only thing that scrolls ─────────────────────── -->
          <div
            class="min-h-0 flex-1 overflow-y-auto overscroll-contain p-4 sm:px-6"
            @focusin="onFocusIn"
          >
            <slot />
          </div>

          <!-- ── Footer: the action, never scrolled away ───────────────── -->
          <div
            v-if="showFooter && slots.footer"
            class="shrink-0 border-t border-default p-4 pb-[calc(1rem+env(safe-area-inset-bottom))] sm:px-6"
          >
            <slot name="footer" />
          </div>
        </div>
      </div>
    </template>
  </component>
</template>
